import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import {
  USER_LOAN_ESTIMATION_TYPE,
  USER_LOAN_PROGRAM,
  USER_OCCUPANCY,
  USER_ROLE,
} from '@/config/constants';
import {
  BuyerDocumentCategory,
  BuyerDocumentRequest,
  BuyerDocumentRequestStatus,
  useCreateBuyerDocumentRequestMutation,
  useDeleteBuyerDocumentRequestMutation,
  useGetBuyerDocumentRequestsQuery,
  useUpdateBuyerDocumentRequestMutation,
} from '@/apis/extended/admin/buyerDocumentRequestsApi';
import { useGetLatestRatesQuery } from '@/apis/extended/admin/interestRateApi';
import { useGetProductsByAdminQuery } from '@/apis/extended/admin/productApi';
import { useGetUsersByAdminQuery } from '@/apis/extended/admin/userApi';
import {
  documentationRequestsForProgramFromRates,
  type DocumentationRequestsByType,
} from '@/utils/document';

const requestTabs = [
  { key: 'all', label: 'All Requests' },
  { key: 'requested', label: 'Requested' },
  { key: 'uploaded', label: 'Uploaded' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
] as const;

const CATEGORY_OPTIONS: BuyerDocumentCategory[] = [
  'income',
  'assets',
  'liabilities',
  'id',
  'credit',
  'explanations',
];

export const AdminDocuments = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const docTablePaperSx = {
    bgcolor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.greyShades.border1}`,
    borderRadius: 2,
    boxShadow: `0 2px 12px ${alpha(theme.palette.common.black, 0.06)}`,
    overflow: 'hidden' as const,
  };

  const docTableHeadRowSx = {
    bgcolor: alpha(theme.palette.primary.main, 0.07),
    '& .MuiTableCell-head': {
      borderBottom: `1px solid ${theme.palette.greyShades.border1}`,
    },
  };

  const outlinedFieldSx = {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.greyShades.border1,
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: alpha(theme.palette.text.primary, 0.22),
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },
  };

  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBuyer, setSelectedBuyer] = useState<any>(null);
  const [requestsDialogOpen, setRequestsDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [lastSyncKey, setLastSyncKey] = useState<string | null>(null);
  const [requestForm, setRequestForm] = useState({
    category: 'income' as BuyerDocumentCategory,
    title: '',
    dueDate: '',
    notes: '',
  });

  const { data: usersData, isLoading } = useGetUsersByAdminQuery({
    limit: 200,
    offset: 0,
  });
  const { data: latestRates } = useGetLatestRatesQuery();
  const buyers = useMemo(
    () =>
      (usersData?.users || []).filter((user) => user.role === USER_ROLE.BUYER),
    [usersData]
  );

  const { data: productsData } = useGetProductsByAdminQuery(
    {
      limit: 200,
      offset: 0,
      userId: selectedBuyer?._id,
    } as any,
    {
      skip: !selectedBuyer?._id,
    }
  );

  const { data: requests, isLoading: isRequestsLoading } =
    useGetBuyerDocumentRequestsQuery(selectedBuyer?._id || '', {
      skip: !selectedBuyer?._id,
    });
  const [createRequest, { isLoading: isCreating }] =
    useCreateBuyerDocumentRequestMutation();
  const [updateRequest] = useUpdateBuyerDocumentRequestMutation();
  const [deleteRequest] = useDeleteBuyerDocumentRequestMutation();

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const filteredBuyers = useMemo(() => {
    if (!buyers) return [];
    return buyers.filter((buyer) => {
      const name = buyer.name || '';
      const email = buyer.email || '';
      return (
        name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    });
  }, [buyers, searchTerm]);

  const activeTabKey = requestTabs[tabValue]?.key || 'all';
  const visibleRequests = useMemo(() => {
    const list = requests || [];
    if (activeTabKey === 'all') return list;
    return list.filter((request) => request.status === activeTabKey);
  }, [requests, activeTabKey]);

  const normalizeLoanProgram = (value?: string) => {
    const programs = Object.values(USER_LOAN_PROGRAM);
    return programs.includes(value as USER_LOAN_PROGRAM)
      ? (value as USER_LOAN_PROGRAM)
      : USER_LOAN_PROGRAM.CONVENTIONAL;
  };

  const categorizeDocumentTitle = (title: string): BuyerDocumentCategory => {
    const lower = title.toLowerCase();
    if (lower.includes('license') || lower.includes('id')) return 'id';
    if (lower.includes('credit')) return 'credit';
    if (
      lower.includes('w-2') ||
      lower.includes('paystub') ||
      lower.includes('income') ||
      lower.includes('tax return')
    )
      return 'income';
    if (
      lower.includes('bank') ||
      lower.includes('asset') ||
      lower.includes('statement') ||
      lower.includes('ira') ||
      lower.includes('401')
    )
      return 'assets';
    if (
      lower.includes('mortgage') ||
      lower.includes('debt') ||
      lower.includes('liability')
    )
      return 'liabilities';
    return 'explanations';
  };

  const computeRequiredDocuments = () => {
    const products = productsData?.products || [];
    const required = new Set<string>();
    const baseDoc = "Driver's license or government issued ID";

    products.forEach((product: any) => {
      const program = normalizeLoanProgram(
        product?.savedLoanType ||
          product?.user?.loanProgram ||
          selectedBuyer?.loanProgram
      );
      const estimationType =
        (product?.user?.loanEstimationType as USER_LOAN_ESTIMATION_TYPE) ||
        (selectedBuyer?.loanEstimationType as USER_LOAN_ESTIMATION_TYPE) ||
        USER_LOAN_ESTIMATION_TYPE.HOME_BUYER;
      const occupancy =
        (product?.user?.occupancy as USER_OCCUPANCY) ||
        (selectedBuyer?.occupancy as USER_OCCUPANCY) ||
        USER_OCCUPANCY.PRIMARY_RESIDENCE;
      const docsByType = documentationRequestsForProgramFromRates(
        program,
        latestRates?.programs as
          | Record<
              string,
              { documentationRequests?: DocumentationRequestsByType }
            >
          | undefined
      );
      const docs =
        docsByType?.[estimationType]?.[occupancy] ||
        docsByType?.[estimationType]?.[USER_OCCUPANCY.PRIMARY_RESIDENCE] ||
        [];

      required.add(baseDoc);
      docs.forEach((doc) => required.add(doc));
    });

    return Array.from(required);
  };

  const syncProgramDocumentRequests = async () => {
    if (!selectedBuyer?._id || !requests) return;
    const requiredDocs = computeRequiredDocuments();
    const requiredKey = requiredDocs
      .map((doc) => doc.toLowerCase())
      .sort()
      .join('|');
    const existingKey = requests
      .map((req) => `${req.title}:${req.status}`)
      .sort()
      .join('|');
    const syncKey = `${selectedBuyer._id}:${requiredKey}:${existingKey}`;
    if (syncKey === lastSyncKey) return;

    const requiredSet = new Set(requiredDocs.map((doc) => doc.toLowerCase()));
    const existingByTitle = new Map(
      requests.map((req) => [req.title.toLowerCase(), req])
    );

    const toCreate = requiredDocs.filter(
      (doc) => !existingByTitle.has(doc.toLowerCase())
    );
    const toDelete = requests.filter(
      (req) =>
        (req.status === 'requested' || req.status === 'rejected') &&
        !requiredSet.has(req.title.toLowerCase())
    );

    if (toCreate.length === 0 && toDelete.length === 0) {
      setLastSyncKey(syncKey);
      return;
    }

    try {
      await Promise.all(
        toCreate.map((title) =>
          createRequest({
            buyerUserId: selectedBuyer._id,
            category: categorizeDocumentTitle(title),
            title,
            notes: 'Auto-requested from loan program defaults',
          }).unwrap()
        )
      );
      await Promise.all(toDelete.map((req) => deleteRequest(req._id).unwrap()));
      setLastSyncKey(syncKey);
    } catch (error) {
      console.error('Failed to sync program document requests:', error);
    }
  };

  const handleOpenRequests = (buyer: any) => {
    setSelectedBuyer(buyer);
    setRequestsDialogOpen(true);
  };

  const handleCloseRequests = () => {
    setRequestsDialogOpen(false);
    setSelectedBuyer(null);
  };

  useEffect(() => {
    if (!requestsDialogOpen || !selectedBuyer?._id) return;
    if (!productsData?.products || !requests) return;
    syncProgramDocumentRequests();
  }, [
    requestsDialogOpen,
    selectedBuyer?._id,
    productsData?.products,
    requests,
  ]);

  const getStatusColor = (status: BuyerDocumentRequestStatus) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'requested':
        return 'warning';
      case 'uploaded':
        return 'info';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleCreateRequest = async () => {
    if (!selectedBuyer?._id || !requestForm.title) return;
    await createRequest({
      buyerUserId: selectedBuyer._id,
      category: requestForm.category,
      title: requestForm.title,
      dueDate: requestForm.dueDate || undefined,
      notes: requestForm.notes || undefined,
    }).unwrap();
    setCreateDialogOpen(false);
    setRequestForm({
      category: 'income',
      title: '',
      dueDate: '',
      notes: '',
    });
  };

  if (isLoading) {
    return (
      <Box>
        <Typography
          variant="h3"
          sx={{ color: 'text.primary', fontWeight: 700, mb: 1 }}
        >
          Document Management
        </Typography>
        <LinearProgress sx={{ mt: 2 }} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h3"
          sx={{
            color: 'text.primary',
            fontWeight: 700,
            mb: 2,
          }}
        >
          Document Management
        </Typography>

        <TextField
          fullWidth
          size="small"
          placeholder="Search by user name or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <i
                  className="fa-light fa-search"
                  style={{ color: theme.palette.text.secondary }}
                />
              </InputAdornment>
            ),
          }}
          sx={{
            maxWidth: isMobile ? '100%' : 420,
            '& .MuiOutlinedInput-root': {
              color: theme.palette.text.primary,
              bgcolor: theme.palette.background.paper,
              borderRadius: 2,
            },
            ...outlinedFieldSx,
          }}
        />
      </Box>

      {isMobile ? (
        <Box sx={{ display: 'grid', gap: 2 }}>
          {filteredBuyers.map((buyer) => (
            <Paper
              key={buyer._id}
              elevation={0}
              sx={{
                p: 2,
                bgcolor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.greyShades.border1}`,
                borderRadius: 2,
                boxShadow: `0 2px 12px ${alpha(
                  theme.palette.common.black,
                  0.06
                )}`,
              }}
            >
              <Typography
                variant="h6"
                sx={{ color: 'text.primary', mb: 0.5, fontWeight: 600 }}
              >
                {buyer.name || 'Buyer'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {buyer.email || '—'}
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={() => handleOpenRequests(buyer)}
                  sx={{ fontWeight: 600, borderRadius: 2 }}
                >
                  Manage Requests
                </Button>
              </Box>
            </Paper>
          ))}
          {!filteredBuyers.length && (
            <Alert severity="info">No buyers found.</Alert>
          )}
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={docTablePaperSx}>
          <Table size="small">
            <TableHead>
              <TableRow sx={docTableHeadRowSx}>
                <TableCell
                  sx={{
                    color: 'text.primary',
                    fontWeight: 600,
                  }}
                >
                  Buyer
                </TableCell>
                <TableCell
                  sx={{
                    color: 'text.primary',
                    fontWeight: 600,
                  }}
                >
                  Email
                </TableCell>
                <TableCell
                  sx={{
                    color: 'text.primary',
                    fontWeight: 600,
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredBuyers.map((buyer) => (
                <TableRow
                  key={buyer._id}
                  sx={{
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.04),
                    },
                  }}
                >
                  <TableCell>
                    <Typography variant="body2" sx={{ color: 'text.primary' }}>
                      {buyer.name || 'Buyer'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ color: 'text.secondary' }}
                    >
                      {buyer.email || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => handleOpenRequests(buyer)}
                      sx={{ fontWeight: 600, borderRadius: 2 }}
                    >
                      Manage Requests
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {!filteredBuyers.length && (
                <TableRow>
                  <TableCell colSpan={3}>
                    <Alert severity="info">No buyers found.</Alert>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={requestsDialogOpen}
        onClose={handleCloseRequests}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ color: 'text.primary', fontWeight: 600 }}>
          Buyer Document Requests
        </DialogTitle>
        <DialogContent>
          {isRequestsLoading && <LinearProgress sx={{ mb: 2 }} />}
          <Box
            sx={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              justifyContent: 'space-between',
              alignItems: isMobile ? 'flex-start' : 'center',
              gap: 2,
              mb: 2,
            }}
          >
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {selectedBuyer?.name || 'Buyer'} • {selectedBuyer?.email || '—'}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setCreateDialogOpen(true)}
              sx={{ fontWeight: 600, borderRadius: 2 }}
            >
              New Request
            </Button>
          </Box>

          <Box
            sx={{
              borderBottom: 1,
              borderColor: theme.palette.greyShades.border1,
            }}
          >
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              sx={{
                '& .MuiTab-root': {
                  color: 'text.secondary',
                  fontWeight: 500,
                  '&.Mui-selected': {
                    color: 'primary.main',
                    fontWeight: 600,
                  },
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: theme.palette.primary.main,
                },
              }}
            >
              {requestTabs.map((tab) => (
                <Tab key={tab.key} label={tab.label} />
              ))}
            </Tabs>
          </Box>

          <Box sx={{ mt: 2, display: 'grid', gap: 2 }}>
            {visibleRequests.map((request: BuyerDocumentRequest) => (
              <Paper
                key={request._id}
                elevation={0}
                sx={{
                  p: 2,
                  bgcolor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.greyShades.border1}`,
                  borderRadius: 2,
                  boxShadow: `0 2px 12px ${alpha(
                    theme.palette.common.black,
                    0.05
                  )}`,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: 2,
                  }}
                >
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{ color: 'text.primary', fontWeight: 600 }}
                    >
                      {request.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Category: {request.category}
                    </Typography>
                    {request.dueDate && (
                      <Typography variant="caption" color="text.secondary">
                        Due: {new Date(request.dueDate).toLocaleDateString()}
                      </Typography>
                    )}
                  </Box>
                  <Chip
                    label={request.status.replace('_', ' ')}
                    size="small"
                    color={getStatusColor(request.status)}
                    sx={{ textTransform: 'capitalize' }}
                  />
                </Box>
                {request.notes && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    {request.notes}
                  </Typography>
                )}
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                    mt: 2,
                  }}
                >
                  {(
                    [
                      'requested',
                      'uploaded',
                      'approved',
                      'rejected',
                    ] as BuyerDocumentRequestStatus[]
                  ).map((status) => (
                    <Button
                      key={status}
                      size="small"
                      variant={
                        request.status === status ? 'contained' : 'outlined'
                      }
                      onClick={() =>
                        updateRequest({
                          requestId: request._id,
                          update: { status },
                        })
                      }
                    >
                      {status.replace('_', ' ')}
                    </Button>
                  ))}
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={() => deleteRequest(request._id)}
                  >
                    Delete
                  </Button>
                </Box>
              </Paper>
            ))}
            {!visibleRequests.length && (
              <Alert severity="info">No requests found.</Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outlined"
            color="inherit"
            onClick={handleCloseRequests}
            sx={{ borderColor: theme.palette.greyShades.border1 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: 'text.primary', fontWeight: 600 }}>
          Create Document Request
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gap: 2, mt: 1 }}>
            <TextField
              label="Title"
              value={requestForm.title}
              onChange={(e) =>
                setRequestForm((prev) => ({ ...prev, title: e.target.value }))
              }
              fullWidth
            />
            <TextField
              select
              label="Category"
              value={requestForm.category}
              onChange={(e) =>
                setRequestForm((prev) => ({
                  ...prev,
                  category: e.target.value as BuyerDocumentCategory,
                }))
              }
              fullWidth
            >
              {CATEGORY_OPTIONS.map((category) => (
                <MenuItem key={category} value={category}>
                  {category}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Due Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={requestForm.dueDate}
              onChange={(e) =>
                setRequestForm((prev) => ({ ...prev, dueDate: e.target.value }))
              }
              fullWidth
            />
            <TextField
              label="Notes"
              multiline
              rows={3}
              value={requestForm.notes}
              onChange={(e) =>
                setRequestForm((prev) => ({ ...prev, notes: e.target.value }))
              }
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => setCreateDialogOpen(false)}
            sx={{ borderColor: theme.palette.greyShades.border1 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleCreateRequest}
            disabled={isCreating || !requestForm.title}
            sx={{ fontWeight: 600, borderRadius: 2 }}
          >
            Create Request
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
